import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { genreOptions, type SpotifyPlaylist } from "../../types/outreach";

interface SpotifyPlaylistEditModalProps {
  open: boolean;
  playlists: SpotifyPlaylist[];
  onClose: () => void;
  onSave: (updates: Record<string, any>) => void;
  saving?: boolean;
  error?: string | null;
}

interface PlaylistFormState {
  playlistName: string;
  playlistUrl: string;
  curatorSocialHandle: string;
  curatorEmail: string;
  source: string;
  playlistLikes: string;
  playlistFollowers: string;
  trackCount: string;
  genres: string[];
}

const sanitizeNumberInput = (value: string): number | null | string => {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  const normalized = trimmed.replace(/,/g, "");
  const parsed = Number(normalized);
  if (Number.isNaN(parsed)) {
    return trimmed;
  }

  return parsed;
};

export const SpotifyPlaylistEditModal: React.FC<
  SpotifyPlaylistEditModalProps
> = ({
  open,
  playlists,
  onClose,
  onSave,
  saving = false,
  error = null,
}) => {
  const isMultiple = playlists.length > 1;

  const baseState = useMemo<PlaylistFormState>(() => {
    const firstPlaylist = playlists[0];
    const initialGenres = firstPlaylist?.genre
      ? firstPlaylist.genre
          .split("/")
          .map((genre) => genre.trim())
          .filter(Boolean)
      : [];

    if (playlists.length === 1 && firstPlaylist) {
      return {
        playlistName: firstPlaylist.playlistName ?? "",
        playlistUrl: firstPlaylist.playlistUrl ?? "",
        curatorSocialHandle: firstPlaylist.curatorSocialHandle ?? "",
        curatorEmail: firstPlaylist.curatorEmail ?? "",
        source: firstPlaylist.source ?? "",
        playlistLikes:
          firstPlaylist.playlistLikes !== undefined &&
          firstPlaylist.playlistLikes !== null
            ? String(firstPlaylist.playlistLikes)
            : "",
        playlistFollowers:
          firstPlaylist.playlistFollowers !== undefined &&
          firstPlaylist.playlistFollowers !== null
            ? String(firstPlaylist.playlistFollowers)
            : "",
        trackCount:
          firstPlaylist.trackCount !== undefined &&
          firstPlaylist.trackCount !== null
            ? String(firstPlaylist.trackCount)
            : "",
        genres: initialGenres,
      };
    }

    return {
      playlistName: "",
      playlistUrl: "",
      curatorSocialHandle: "",
      curatorEmail: "",
      source: "",
      playlistLikes: "",
      playlistFollowers: "",
      trackCount: "",
      genres: initialGenres,
    };
  }, [playlists]);

  const [formState, setFormState] = useState<PlaylistFormState>(baseState);

  useEffect(() => {
    if (open) {
      setFormState(baseState);
    }
  }, [baseState, open]);

  const handleFieldChange = (
    field: keyof Omit<PlaylistFormState, "genres">
  ) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      setFormState((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

  const handleGenresChange = (_: unknown, genres: string[]) => {
    setFormState((prev) => ({
      ...prev,
      genres,
    }));
  };

  const handleSubmit = () => {
    const updates: Record<string, any> = {
      genre: formState.genres.join("/"),
    };

    if (!isMultiple) {
      updates.playlistName = formState.playlistName.trim();
      updates.playlistUrl = formState.playlistUrl.trim();
      updates.curatorSocialHandle = formState.curatorSocialHandle.trim();
      updates.curatorEmail = formState.curatorEmail.trim();
      updates.source = formState.source.trim();

      const likes = sanitizeNumberInput(formState.playlistLikes);
      const followers = sanitizeNumberInput(formState.playlistFollowers);
      const trackCount = sanitizeNumberInput(formState.trackCount);

      updates.playlistLikes = likes;
      updates.playlistFollowers = followers;
      updates.trackCount = trackCount;
    }

    onSave(updates);
  };

  const canSave = useMemo(() => formState.genres.length > 0, [formState.genres]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isMultiple ? "Edit Spotify Playlists" : "Edit Spotify Playlist"}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}

          {isMultiple ? (
            <Typography variant="body2" color="text.secondary">
              Updating {playlists.length} playlists. Only genre can be edited
              for multiple selections.
            </Typography>
          ) : (
            <Stack spacing={2}>
              <TextField
                label="Playlist Name"
                value={formState.playlistName}
                onChange={handleFieldChange("playlistName")}
                fullWidth
              />
              <TextField
                label="Playlist URL"
                value={formState.playlistUrl}
                onChange={handleFieldChange("playlistUrl")}
                fullWidth
              />
              <TextField
                label="Curator Social Handle"
                value={formState.curatorSocialHandle}
                onChange={handleFieldChange("curatorSocialHandle")}
                fullWidth
              />
              <TextField
                label="Curator Email"
                value={formState.curatorEmail}
                onChange={handleFieldChange("curatorEmail")}
                fullWidth
              />
              <TextField
                label="Source"
                value={formState.source}
                onChange={handleFieldChange("source")}
                fullWidth
              />
              <Stack direction="row" spacing={2}>
                <TextField
                  label="Playlist Likes"
                  value={formState.playlistLikes}
                  onChange={handleFieldChange("playlistLikes")}
                  fullWidth
                />
                <TextField
                  label="Followers"
                  value={formState.playlistFollowers}
                  onChange={handleFieldChange("playlistFollowers")}
                  fullWidth
                />
              </Stack>
              <TextField
                label="Track Count"
                value={formState.trackCount}
                onChange={handleFieldChange("trackCount")}
                fullWidth
              />
            </Stack>
          )}

          <Autocomplete
            multiple
            options={genreOptions}
            value={formState.genres}
            onChange={handleGenresChange}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Genres"
                placeholder="Select genres"
                helperText="Choose one or more genres that best describe the playlist"
              />
            )}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSave || saving}
        >
          {saving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SpotifyPlaylistEditModal;
